'use client'

import {
  IconChevronRight,
  IconPersonFemale01Color,
  IconPersonFemale02Color,
  IconPersonMale01Color,
  IconPersonMale02Color,
} from '@macarongfactory/web-icons'
import { Divider, OutlineButton, Text } from '@macarongfactory/web-ui'
import { Fragment, useState } from 'react'

import { ExpandFooter } from '@/entities/place'

import StarRating from './StarRating'
import { LOCK_AB_AF_SUB2 } from '../../_features/modoocar-lock-ab'
import {
  MODOOCAR_CODE,
  MODOOCAR_EVENT,
  MODOOCAR_REVIEW_APP_ONELINK_URL,
  REVIEW_LABEL_MAX_LENGTH,
  getLockReviewStatus,
  useModoocarAppLink,
  useModoocarImpression,
  useModoocarTracking,
} from '../_features/modoocar'

import type { IOwnerReview, TReviewAvatar } from '../_features/modoocar'

interface IReviewListProps {
  reviews: IOwnerReview[]
  visibleCount?: number
  totalCount?: number
  appCtaMessage?: string
  isInApp?: boolean
  onMoreClick?: () => void
  expandable?: boolean
  locked?: boolean
}

const AVATAR_ICON_SIZE_PX = 40
const REVIEW_STAR_SIZE_PX = 16
const VISIBLE_BEFORE_EXPAND = 3

const EXPAND_BUTTON_TEXT = '더보기'
const IN_APP_MORE_BUTTON_TEXT = '전체보기'
const IN_APP_CHEVRON_SIZE_PX = 20
const COLLAPSE_BUTTON_TEXT = '닫기'
const APP_CTA_BUTTON_TEXT = '앱에서 전체보기'
const APP_CTA_TRACK_LABEL = '앱에서 더보기'
const LOCK_REVIEW_LABEL = '오너 리뷰 잠금'

const AVATAR_ICON: Record<TReviewAvatar, typeof IconPersonMale01Color> = {
  'male-1': IconPersonMale01Color,
  'male-2': IconPersonMale02Color,
  'female-1': IconPersonFemale01Color,
  'female-2': IconPersonFemale02Color,
}

const ReviewItem = ({ review }: { review: IOwnerReview }) => {
  const Avatar = AVATAR_ICON[review.avatar ?? 'male-1']
  const [photoError, setPhotoError] = useState(false)
  const showPhoto = Boolean(review.authorPhotoUrl) && !photoError

  return (
    <div className='flex flex-col gap-dimension-x3 px-dimension-x5 py-dimension-x4'>
      <div className='flex items-center gap-dimension-x3'>
        <div className='flex h-dimension-x10 w-dimension-x10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-background-surface-basement'>
          {showPhoto ? (
            <img
              // [라이브러리]: React는 하이드레이션 전에 실패한 이미지의 onError를 호출하지 않아서, 마운트할 때 이미 깨진 이미지도 아바타로 바꿉니다.
              ref={img => {
                if (img && img.complete && img.naturalWidth === 0) {
                  setPhotoError(true)
                }
              }}
              src={review.authorPhotoUrl}
              alt=''
              className='h-full w-full object-cover'
              onError={() => setPhotoError(true)}
            />
          ) : (
            <Avatar width={AVATAR_ICON_SIZE_PX} height={AVATAR_ICON_SIZE_PX} />
          )}
        </div>
        <div className='flex flex-1 flex-col gap-dimension-x0-5'>
          {review.carModel ? (
            <Text
              variant='title-2-semibold'
              color='text-foreground-neutral-strong'
            >
              {review.carModel}
            </Text>
          ) : null}
          {review.date ? (
            <Text
              variant='body-2-medium'
              color='text-foreground-neutral-alternative'
            >
              {review.date}
            </Text>
          ) : null}
          {review.rating ? (
            <StarRating
              rating={review.rating}
              size={REVIEW_STAR_SIZE_PX}
              showValue
            />
          ) : null}
        </div>
      </div>
      <Text
        variant='title-2-medium'
        color='text-foreground-neutral-strong'
        className='whitespace-pre-line break-keep'
      >
        {review.content}
      </Text>
    </div>
  )
}

const TrackedReviewItem = ({
  review,
  rank,
}: {
  review: IOwnerReview
  rank: number
}) => {
  const { itemShow } = useModoocarTracking()
  const ref = useModoocarImpression<HTMLDivElement>(() =>
    itemShow(
      MODOOCAR_CODE.OWNER_REVIEW,
      'review',
      `리뷰 내용 | ${review.content.slice(0, REVIEW_LABEL_MAX_LENGTH)}`,
      { rank }
    )
  )
  return (
    <div ref={ref}>
      <ReviewItem review={review} />
    </div>
  )
}

const AppCtaOverlay = ({
  review,
  message,
  onClick,
  locked = false,
}: {
  review: IOwnerReview
  message: string
  onClick: () => void
  locked?: boolean
}) => (
  <div className='relative'>
    <div className='absolute inset-0 overflow-clip'>
      <ReviewItem review={review} />
    </div>
    <div
      className={`relative z-10 flex flex-col items-center gap-dimension-x3 bg-[rgba(255,255,255,0.8)] backdrop-blur-[4px] ${
        locked
          ? 'px-dimension-x16 py-dimension-x12'
          : 'py-[52px] pl-[65px] pr-[64px]'
      }`}
    >
      <p className='whitespace-pre-line text-center text-[16px] font-semibold leading-[24px] text-foreground-neutral-strong'>
        {message}
      </p>
      <OutlineButton
        text={APP_CTA_BUTTON_TEXT}
        size='medium'
        variant='brand'
        clickable={onClick}
      />
    </div>
  </div>
)

const ReviewList = ({
  reviews,
  visibleCount = VISIBLE_BEFORE_EXPAND,
  totalCount,
  appCtaMessage,
  isInApp = false,
  onMoreClick,
  expandable = false,
  locked = false,
}: IReviewListProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const { itemShow, itemClick, track } = useModoocarTracking()
  const total = totalCount ?? reviews.length
  const hasMore = total > visibleCount
  // [정책]: 잠금 화면(B)은 후기가 미리보기 건수(visibleCount)를 넘을 때만 잠금을 그립니다. 그 이하인 화면은 디자인이 정해지지 않았습니다.
  const showLock = locked && !isInApp && hasMore
  const lockReviewStatus = getLockReviewStatus(total)
  const openReviewMoreLink = useModoocarAppLink({
    fallbackUrl: MODOOCAR_REVIEW_APP_ONELINK_URL,
    afSub2: LOCK_AB_AF_SUB2.REVIEW_MORE,
  })
  const openLockReviewLink = useModoocarAppLink({
    fallbackUrl: MODOOCAR_REVIEW_APP_ONELINK_URL,
    afSub2: LOCK_AB_AF_SUB2.LOCK_REVIEW,
  })
  const lockShowRef = useModoocarImpression<HTMLDivElement>(
    () =>
      track(MODOOCAR_EVENT.SECTION_SHOW, {
        code: MODOOCAR_CODE.LOCK_REVIEW,
        type: 'lock',
        label: LOCK_REVIEW_LABEL,
        status: lockReviewStatus,
      }),
    { enabled: showLock }
  )
  const moreShowRef = useModoocarImpression<HTMLDivElement>(() =>
    itemShow(MODOOCAR_CODE.OWNER_REVIEW, 'button', '리뷰 전체보기')
  )
  const appCtaShowRef = useModoocarImpression<HTMLDivElement>(() =>
    itemShow(MODOOCAR_CODE.OWNER_REVIEW, 'button', APP_CTA_TRACK_LABEL)
  )
  const appCtaText =
    appCtaMessage ??
    `마이클 앱에서\n${total.toLocaleString()}개 오너 리뷰를 볼 수 있어요`

  if (isInApp) {
    const visibleReviews = reviews.slice(0, visibleCount)
    return (
      <div className='flex flex-col'>
        {visibleReviews.map((review, index) => (
          <Fragment key={review.id}>
            {index > 0 ? (
              <div className='px-dimension-x5'>
                <Divider.Horizontal />
              </div>
            ) : null}
            <TrackedReviewItem review={review} rank={index + 1} />
          </Fragment>
        ))}
        {hasMore ? (
          <div className='flex flex-col px-dimension-x5'>
            <Divider.Horizontal />
            <div
              ref={moreShowRef}
              className='flex min-h-[58px] items-center justify-center py-dimension-x3'
            >
              <button
                type='button'
                onClick={() => {
                  itemClick(
                    MODOOCAR_CODE.OWNER_REVIEW,
                    'button',
                    '리뷰 전체보기'
                  )
                  onMoreClick?.()
                }}
                className='inline-flex items-center gap-dimension-x0-5 rounded-dimension-x2 px-dimension-x1-5 py-dimension-x1'
              >
                <Text
                  variant='title-1-medium'
                  color='text-foreground-neutral-alternative'
                >
                  {IN_APP_MORE_BUTTON_TEXT}
                </Text>
                <IconChevronRight
                  width={IN_APP_CHEVRON_SIZE_PX}
                  height={IN_APP_CHEVRON_SIZE_PX}
                  className='text-foreground-neutral-alternative'
                />
              </button>
            </div>
          </div>
        ) : null}
      </div>
    )
  }

  // [정책]: 웹에서는 '더보기'로 펼쳐도 후기를 3건까지만 보여 주고, 마지막에 후기 1건을 흐리게 깔아 앱 설치 CTA를 얹습니다.
  // 잠금 화면(B)은 펼치지 않고 처음부터 같은 구조로 잠금 프레임을 보여 줍니다.
  const showAppCta = showLock || (expandable && hasMore && isExpanded)
  const visibleReviews = showAppCta
    ? reviews.slice(0, visibleCount + 1)
    : reviews.slice(0, visibleCount)
  const lastIndex = visibleReviews.length - 1

  // [앱]: 모바일은 location.href로 이동해야 iOS 유니버설 링크가 앱을 엽니다.
  const handleAppCtaClick = () =>
    showLock
      ? openLockReviewLink(() =>
          itemClick(MODOOCAR_CODE.LOCK_REVIEW, 'button', APP_CTA_BUTTON_TEXT, {
            status: lockReviewStatus,
          })
        )
      : openReviewMoreLink(() =>
          itemClick(MODOOCAR_CODE.OWNER_REVIEW, 'button', APP_CTA_TRACK_LABEL)
        )

  return (
    <div className='flex flex-col'>
      {visibleReviews.map((review, index) => {
        const isLast = showAppCta && index === lastIndex
        return (
          <Fragment key={review.id}>
            {index > 0 ? (
              <div className='px-dimension-x5'>
                <Divider.Horizontal />
              </div>
            ) : null}
            {isLast ? (
              <div ref={showLock ? lockShowRef : appCtaShowRef}>
                <AppCtaOverlay
                  review={review}
                  message={appCtaText}
                  onClick={handleAppCtaClick}
                  locked={showLock}
                />
              </div>
            ) : (
              <TrackedReviewItem review={review} rank={index + 1} />
            )}
          </Fragment>
        )
      })}

      {expandable && hasMore && !showLock ? (
        <div className='px-dimension-x5'>
          <ExpandFooter
            text={isExpanded ? COLLAPSE_BUTTON_TEXT : EXPAND_BUTTON_TEXT}
            chevron={isExpanded ? 'up' : 'down'}
            onClick={() => setIsExpanded(prev => !prev)}
          />
        </div>
      ) : null}
    </div>
  )
}

export default ReviewList
